import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Users,
  CheckCircle2,
  DollarSign,
  Columns,
  Loader2,
  Sliders
} from 'lucide-react'
import { PageContainer, PageHeader } from '@/components/common'
import { useUploadWorkflowController } from '@/hooks/useUploadWorkflowController'
import { StagingWorkflowStep } from '@/hooks/useUploadWorkflowState'
import { ConfigurationStep } from '@/components/upload/workflow/ConfigurationStep'
import { DriverReviewStep } from '@/components/upload/workflow/DriverReviewStep'
import { DriverActionsConfirmationStep } from '@/components/upload/workflow/DriverActionsConfirmationStep'
import { FieldMappingStep } from '@/components/upload/workflow/FieldMappingStep'
import { EarningsCreationStep } from '@/components/upload/workflow/EarningsCreationStep'
import { WorkflowLayout } from '@/components/upload/workflow/WorkflowLayout'

const STAGING_STEPS = [
  { key: 'configuration' as StagingWorkflowStep, label: 'Konfiguration', icon: Sliders },
  { key: 'field-mapping' as StagingWorkflowStep, label: 'Kolumnidentifiering', icon: Columns },
  { key: 'driver-review' as StagingWorkflowStep, label: 'Förargranskning', icon: Users },
  { key: 'driver-confirmation' as StagingWorkflowStep, label: 'Bekräftelse', icon: CheckCircle2 },
  { key: 'earnings-creation' as StagingWorkflowStep, label: 'Skapa Inkomster', icon: DollarSign },
]

export function UploadWorkflowPage() {
  const { uploadId } = useParams<{ uploadId: string }>()
  const {
    state: workflow,
    updateState,
    setStep,
    saveConfiguration,
    runFieldMapping,
    ensureDriverMatches,
    processDriverActions,
    ignoreRow,
    unignoreRow,
    canProceedToNextStep
  } = useUploadWorkflowController(uploadId)

  useEffect(() => {
    if (
      workflow.step === 'driver-review' &&
      workflow.uploadStaging?.normalizedData?.length &&
      workflow.driverMatches.length === 0 &&
      !workflow.stagingLoading
    ) {
      ensureDriverMatches()
    }
  }, [
    workflow.step,
    workflow.uploadStaging?.normalizedData?.length,
    workflow.driverMatches.length,
    workflow.stagingLoading,
    ensureDriverMatches
  ])

  const canGoBack = (): boolean => {
    return workflow.step !== 'configuration' &&
      workflow.step !== 'complete' &&
      !workflow.isProcessingDriverActions &&
      !workflow.isCreatingEarnings
  }

  const handleNext = async () => {
    if (!canProceedToNextStep()) return

    const currentStep = workflow.step

    if (currentStep === 'configuration') {
      await saveConfiguration(workflow.selectedPlatform, workflow.selectedPeriod)
      setStep(workflow.shouldSkipFieldMapping ? 'driver-review' : 'field-mapping')
      return
    }

    if (currentStep === 'field-mapping') {
      await runFieldMapping()
      setStep('driver-review')
      return
    }

    if (currentStep === 'driver-review') {
      setStep('driver-confirmation')
      return
    }

    if (currentStep === 'driver-confirmation') {
      await processDriverActions()
      return
    }

    const currentIndex = STAGING_STEPS.findIndex(step => step.key === currentStep)
    if (currentIndex < STAGING_STEPS.length - 1) {
      const nextStep = STAGING_STEPS[currentIndex + 1].key
      setStep(nextStep)
    }
  }

  const handleBack = () => {
    if (!canGoBack()) return

    const currentIndex = STAGING_STEPS.findIndex(step => step.key === workflow.step)
    if (currentIndex > 0) {
      const prevStep = STAGING_STEPS[currentIndex - 1].key
      setStep(prevStep)
    }
  }

  const getNextButtonLabel = (): string => {
    if (workflow.isProcessingDriverActions) return 'Bearbetar förare...'
    if (workflow.isCreatingEarnings) return 'Skapar inkomster...'
    if (workflow.step === 'driver-confirmation') return 'Spara inkomster'
    return 'Nästa'
  }

  if (workflow.stagingLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-24">
          <div className="text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
            <div>
              <div className="font-medium">
                {!workflow.uploadStaging ? 'Laddar staging data...' : 'Kör förarmatchning...'}
              </div>
              {workflow.processingProgress && (
                <div className="text-sm text-muted-foreground mt-2">
                  {workflow.processingProgress.status} ({workflow.processingProgress.percentage}%)
                </div>
              )}
            </div>
          </div>
        </div>
      </PageContainer>
    )
  }

  if (!workflow.uploadStaging || !workflow.originalUpload) {
    return (
      <PageContainer>
        <div className="text-center py-24">
          <p className="text-muted-foreground">Kunde inte ladda staging data</p>
        </div>
      </PageContainer>
    )
  }

  const currentStepIndex = workflow.step === 'complete'
    ? STAGING_STEPS.length - 1
    : STAGING_STEPS.findIndex(step => step.key === workflow.step)

  const getStepDescription = (): string => {
    switch (workflow.step) {
      case 'configuration':
        return 'Välj plattform och tidsperiod för denna uppladdning'
      case 'field-mapping':
        return 'Identifiera och mappa kolumner från din fil till systemfält'
      case 'driver-review':
        return 'Granska hur förare matchades och välj åtgärder för omatchade förare'
      case 'driver-confirmation':
        return 'Bekräfta alla åtgärder innan förare skapas eller uppdateras'
      case 'earnings-creation':
        return 'Skapar inkomstposter baserat på dina val'
      case 'complete':
        return 'Uppladdningen är klar'
      default:
        return ''
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title={STAGING_STEPS[currentStepIndex].label}
        subtitle={getStepDescription()}
      />
      <WorkflowLayout
        fileName={`${workflow.originalUpload.fileName} - ${workflow.uploadStaging.platform}`}
        currentStepIndex={currentStepIndex}
        steps={STAGING_STEPS}
        canGoBack={canGoBack()}
        canGoNext={canProceedToNextStep()}
        isProcessing={workflow.isProcessingDriverActions || workflow.isCreatingEarnings}
        onBack={handleBack}
        onNext={handleNext}
        nextButtonLabel={getNextButtonLabel()}
        showCompleteButton={workflow.step === 'complete'}
      >
        {workflow.step === 'configuration' && (
          <ConfigurationStep
            selectedPlatform={workflow.selectedPlatform}
            selectedPeriod={workflow.selectedPeriod}
            fileName={workflow.originalUpload.fileName}
            onPlatformChange={(platform) => updateState({ selectedPlatform: platform })}
            onPeriodChange={(period) => updateState({ selectedPeriod: period })}
          />
        )}

        {workflow.step === 'field-mapping' && (
          <FieldMappingStep
            upload={workflow.originalUpload}
            identifierMapping={workflow.identifierMapping}
            fieldMapping={workflow.fieldMapping}
            onIdentifierMappingChange={(mapping) => updateState({ identifierMapping: mapping })}
            onFieldMappingChange={(mapping) => updateState({ fieldMapping: mapping })}
          />
        )}

        {workflow.step === 'driver-review' && (
          <DriverReviewStep
            upload={workflow.originalUpload}
            driverMatches={workflow.driverMatches}
            availableDrivers={workflow.availableDrivers}
            unmatchedActions={workflow.unmatchedActions}
            perfectMatchOverrides={workflow.perfectMatchOverrides}
            ignoredRows={workflow.ignoredRows}
            autoAcceptMatches={workflow.autoAcceptMatches}
            skippedFieldMapping={workflow.skippedSteps['field-mapping']}
            onUnmatchedActionChange={(rowIndex, action, data) => {
              const next = new Map(workflow.unmatchedActions)
              next.set(rowIndex, { rowIndex, action, ...data })
              updateState({ unmatchedActions: next })
            }}
            onPerfectMatchOverrideChange={(rowIndex, action, data) => {
              const next = new Map(workflow.perfectMatchOverrides)
              next.set(rowIndex, { rowIndex, action, ...data })
              updateState({ perfectMatchOverrides: next })
            }}
            onRemovePerfectMatchOverride={(rowIndex) => {
              const next = new Map(workflow.perfectMatchOverrides)
              next.delete(rowIndex)
              updateState({ perfectMatchOverrides: next })
            }}
            onIgnoreRow={ignoreRow}
            onUnignoreRow={unignoreRow}
            onAutoAcceptMatchesChange={(value) => updateState({ autoAcceptMatches: value })}
          />
        )}

        {workflow.step === 'driver-confirmation' && (
          <DriverActionsConfirmationStep
            driverMatches={workflow.driverMatches}
            availableDrivers={workflow.availableDrivers}
            unmatchedActions={workflow.unmatchedActions}
            perfectMatchOverrides={workflow.perfectMatchOverrides}
            ignoredRows={workflow.ignoredRows}
            autoAcceptMatches={workflow.autoAcceptMatches}
            platform={workflow.uploadStaging.platform}
            skippedFieldMapping={workflow.skippedSteps['field-mapping']}
            skippedDriverReview={workflow.skippedSteps['driver-review']}
          />
        )}

        {workflow.step === 'earnings-creation' && (
          <EarningsCreationStep
            isProcessing={workflow.isCreatingEarnings}
            progress={workflow.processingProgress}
            result={workflow.earningsResults}
            totalRows={workflow.driverMatches.length}
          />
        )}

        {workflow.step === 'complete' && workflow.earningsResults && (
          <EarningsCreationStep
            isProcessing={false}
            progress={null}
            result={workflow.earningsResults}
            totalRows={workflow.driverMatches.length}
          />
        )}
      </WorkflowLayout>
    </PageContainer>
  )
}
